// AppShellV2 — top-level layout for v2 routes. Renders the top bar, the
// 48px view rail, and an <Outlet /> that hosts the active route's view.
// The view itself owns its side panels — there is no conditional rendering
// of explorer panels at this level.
//
// Usage:
//   <Route element={<AppShellV2 appBar={...} rail={...} />}>
//     <Route path="dashboards" element={<GeneratedDashboardViewV2 />} />
//     ...
//   </Route>

import React from 'react';
import { Outlet } from 'react-router-dom';
import Box from '@mui/material/Box';

export interface AppShellV2Props {
    /** Rendered at the top, full width. Typically <AppBarV2 ... />. */
    appBar?: React.ReactNode;
    /** Rendered on the left, full height under the top bar. Typically <AppRailV2 ... />. */
    rail?: React.ReactNode;
    /**
     * Optional content rendered in place of <Outlet />. Useful for tests or
     * when nesting an alternate router. Defaults to <Outlet />.
     */
    children?: React.ReactNode;
}

export const AppShellV2: React.FC<AppShellV2Props> = ({ appBar, rail, children }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                width: '100vw',
                overflow: 'hidden',
            }}
        >
            {appBar}
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {rail}
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {children ?? <Outlet />}
                </Box>
            </Box>
        </Box>
    );
};

AppShellV2.displayName = 'AppShellV2';
